import { type ReactNode } from 'react';

export function Card({ children, className = '' }: { children: ReactNode; className?: string }): JSX.Element {
  return (
    <div className={`rounded-lg border border-border bg-surface p-4 ${className}`}>{children}</div>
  );
}

export function PageHeader({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle?: string;
  right?: ReactNode;
}): JSX.Element {
  return (
    <div className="mb-6 flex items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold text-brand-navy">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-text-soft">{subtitle}</p>}
      </div>
      {right && <div>{right}</div>}
    </div>
  );
}

export function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  disabled,
  className = '',
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  disabled?: boolean;
  className?: string;
}): JSX.Element {
  const base = 'inline-flex items-center rounded-md px-3 py-2 text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed';
  const styles: Record<string, string> = {
    primary: 'bg-brand-green text-white hover:bg-brand-green/90',
    secondary: 'border border-border bg-surface text-brand-navy hover:bg-bg',
    danger: 'bg-destructive text-white hover:bg-destructive/90',
    ghost: 'text-text-soft hover:bg-bg hover:text-brand-navy',
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`${base} ${styles[variant]} ${className}`}>
      {children}
    </button>
  );
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger';
}): JSX.Element {
  const styles: Record<string, string> = {
    neutral: 'bg-bg text-text-soft',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    danger: 'bg-destructive/10 text-destructive',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${styles[tone]}`}>
      {children}
    </span>
  );
}

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return (
    <input
      {...props}
      className={
        'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-brand-navy outline-none focus:border-brand-green ' +
        (props.className ?? '')
      }
    />
  );
}

export function Label({ children, htmlFor }: { children: ReactNode; htmlFor?: string }): JSX.Element {
  return (
    <label htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-text-soft">
      {children}
    </label>
  );
}

export function Loading(): JSX.Element {
  return <div className="text-sm text-text-soft">Loading…</div>;
}

export function ErrorBox({ message }: { message: string }): JSX.Element {
  return (
    <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{message}</div>
  );
}

export function EmptyState({ children }: { children: ReactNode }): JSX.Element {
  return <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-text-soft">{children}</div>;
}

export function formatDate(ms: number | null): string {
  if (!ms) return '—';
  const d = new Date(ms);
  return d.toISOString().slice(0, 16).replace('T', ' ');
}
